import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

export interface FiscalEmission {
  id: string;
  numero: number;
  serie: number;
  chave_acesso: string | null;
  valor_total: number;
  created_at: string;
  order_id: string | null;
}

interface UseFiscalEmissionsReportParams {
  dateFrom: Date;
  dateTo: Date;
}

export function useFiscalEmissionsReport({ dateFrom, dateTo }: UseFiscalEmissionsReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["fiscal-emissions-report", activeRestaurant?.id, dateFrom.toISOString(), dateTo.toISOString()],
    queryFn: async () => {
      if (!activeRestaurant?.id) return { items: [], totalNotes: 0, totalValue: 0 };

      const fromStr = dateFrom.toISOString().split("T")[0] + "T00:00:00";
      const toStr = dateTo.toISOString().split("T")[0] + "T23:59:59";

      const { data, error } = await supabase
        .from("nfce_issued")
        .select("id, numero, serie, chave_acesso, valor_total, created_at, order_id")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "authorized")
        .gte("created_at", fromStr)
        .lte("created_at", toStr)
        .order("created_at", { ascending: false })
        .limit(1000);

      if (error) throw error;

      const items: FiscalEmission[] = data || [];
      const totalValue = items.reduce((sum, item) => sum + (item.valor_total || 0), 0);

      return {
        items,
        totalNotes: items.length,
        totalValue,
      };
    },
    enabled: !!activeRestaurant?.id,
  });
}
